/**
 * Locale plumbing shared by every date/number rendered in this MFE.
 *
 * `Language` lives here (rather than in the context) so the JWT/culture
 * helpers, the translation table and the context can all depend on it without
 * importing React.
 */

export type Language = "en" | "fr"

/** The .NET/Intl culture the FBO host uses for each supported language. */
const INTL_LOCALES: Record<Language, string> = {
  en: "en-CA",
  fr: "fr-CA",
}

/** The BCP-47 tag to hand to Intl / toLocaleString for the current language. */
export function toIntlLocale(language: Language): string {
  return INTL_LOCALES[language]
}

/**
 * The host puts its own culture on the iframe URL (`?culture=en-CA`), which is
 * the only language signal available before the token handshake completes —
 * so the first paint is already in the operator's language. Anything else
 * (missing, `en-US`, junk) is English, exactly as the JWT claim mapping does.
 */
export function getLanguageFromCulture(search: string | null | undefined): Language | null {
  if (!search) return null
  const culture = new URLSearchParams(search).get("culture")
  if (!culture) return null
  return culture.toLowerCase().startsWith("fr") ? "fr" : "en"
}

/**
 * Date + time in the current language. Every timestamp in the UI comes from
 * the API as an ISO string; an unparseable one is shown verbatim rather than
 * as "Invalid Date".
 */
export function formatDateTime(value: string | Date | null | undefined, language: Language): string {
  if (value === null || value === undefined || value === "") return "—"
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString(toIntlLocale(language))
}

/** Date only (no time), in the current language. */
export function formatDate(value: string | Date | null | undefined, language: Language): string {
  if (value === null || value === undefined || value === "") return "—"
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString(toIntlLocale(language))
}

/** Counts and totals — fr-CA groups thousands with a space, en-CA with a comma. */
export function formatNumber(value: number, language: Language): string {
  return value.toLocaleString(toIntlLocale(language))
}
